/**
 * CrewMemberDetail
 * Bio panel shown when a crew member is selected from CrewProfileScreen.
 * Renders the front-profile photo, flag/role, and bio body (paragraph/bullets blocks).
 */

import { useState, useEffect } from 'react'
import { parseHighlights } from './Subheading'
import { playSound } from '../../utils/soundFX'

function BioBody({ body, hideBullets = false }) {
  if (!body?.length) return null
  return (
    <div className="crew-detail-body">
      {body.map((block, i) => {
        if (block.type === 'paragraph') {
          return <p key={i} className="crew-detail-paragraph">{parseHighlights(block.text)}</p>
        }
        if (block.type === 'bullets') {
          if (hideBullets) return null
          return (
            <ul key={i} className="crew-detail-bullets">
              {block.items.map((item, j) => (
                <li key={j} className="crew-detail-bullet">{parseHighlights(item)}</li>
              ))}
            </ul>
          )
        }
        return null
      })}
    </div>
  )
}

export function CrewMemberDetail({ member, contentPosition, useBulletsAsFacts = false }) {
  const [factIndex, setFactIndex] = useState(0)
  // Bumped on any manual prev/next so the auto-rotate timer below restarts —
  // the 30s countdown only fires when the user hasn't interacted.
  const [interactionTick, setInteractionTick] = useState(0)
  // On mobile the fun-fact carousel cycles the bio bullet items instead of funFacts
  const bulletItems = (member.bio?.body || [])
    .filter(b => b.type === 'bullets')
    .flatMap(b => b.items)
  const facts = useBulletsAsFacts ? bulletItems : (member.funFacts || [])

  const handlePrevFact = () => {
    playSound('DataPoint_Close')
    setFactIndex(i => (i - 1 + facts.length) % facts.length)
    setInteractionTick(t => t + 1)
  }

  const handleNextFact = () => {
    playSound('DataPoint_Open')
    setFactIndex(i => (i + 1) % facts.length)
    setInteractionTick(t => t + 1)
  }

  // Auto-advance to the next fun fact every 30s of inactivity. Restarts whenever
  // the user clicks prev/next (interactionTick changes) or the member changes.
  useEffect(() => {
    if (facts.length < 2) return
    const id = setInterval(() => {
      setFactIndex(i => (i + 1) % facts.length)
    }, 30000)
    return () => clearInterval(id)
  }, [facts.length, interactionTick, member.id])

  return (
    <div className="crew-member-detail">
      <div className={`crew-detail-content crew-detail-content--pos-${contentPosition}`}>
        {/* Scroll lives on this inner wrapper, NOT on .crew-detail-content, so the
            outer frame can stay overflow: visible and let the fun-fact prev/next
            buttons hang outside the box without being clipped. */}
        <div className="crew-detail-scroll-inner">
          <div className="crew-detail-header">
            <img src={member.flag} alt="" className="crew-detail-flag" />
            <div>
              <h2 className="crew-detail-name">{member.name}</h2>
              <span className="crew-detail-role">{member.role} — {member.agency}</span>
            </div>
          </div>
          <BioBody body={member.bio?.body} hideBullets={useBulletsAsFacts} />
          {facts.length > 0 && (
            <div className="crew-detail-funfact">
              <button className="crew-detail-funfact-nav" onClick={handlePrevFact} aria-label="Previous fun fact">
                <span className="rect_button-chevrons crew-detail-funfact-nav-icon">&lt;&lt;&lt;</span>
              </button>
              <div className="crew-detail-funfact-content">
                <span className="crew-detail-funfact-label">FUN FACT:</span>
                <span className="crew-detail-funfact-text">{facts[factIndex]}</span>
              </div>
              <button className="crew-detail-funfact-nav" onClick={handleNextFact} aria-label="Next fun fact">
                <span className="rect_button-chevrons crew-detail-funfact-nav-icon">&gt;&gt;&gt;</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
